// src/pages/pentest/dashboard/PentestScope.jsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  Typography,
  CircularProgress,
  Stack,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  Grid,
  Paper,
  Button,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PublicIcon from '@mui/icons-material/Public';
import DnsIcon from '@mui/icons-material/Dns';
import LanIcon from '@mui/icons-material/Lan';
import RouteIcon from '@mui/icons-material/Route';
import LinkIcon from '@mui/icons-material/Link';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useNavigate, useParams } from 'react-router-dom';
import { alpha , darken, lighten} from '@mui/material/styles';
import { BASEURL ,PRIMARYCOLOR,SECONDARYCOLOR,logoURL,COMPANY } from '../../Constants';
import Nav from './nav';

// --- helpers -----------------------------------------------------

const ipv4Re =
  /^(25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)){3}$/;
const cidrRe =
  /^(25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)){3}\/([0-9]|[12]\d|3[0-2])$/;
const domainRe =
  /^(?=.{1,253}$)(([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63})$/i;

const classifyAsset = (s) => {
  const v = (s || '').trim();
  if (!v) return { type: 'unknown', value: v };
  if (cidrRe.test(v)) return { type: 'cidr', value: v };
  if (ipv4Re.test(v)) return { type: 'ip', value: v };
  const noProto = v.replace(/^https?:\/\//i, '').replace(/\/.*$/, '');
  if (domainRe.test(noProto)) return { type: 'domain', value: noProto };
  // Also accept already-wildcarded domains: *.example.com
  const wild = noProto.replace(/^\*\./, '');
  if (noProto.startsWith('*.') && domainRe.test(wild)) return { type: 'domain', value: wild, wasWildcard: true };
  return { type: 'unknown', value: v };
};

const typeMeta = {
  domain: { label: 'DOMAIN', color: PRIMARYCOLOR, icon: <PublicIcon fontSize="small" /> },
  ip: { label: 'IP', color: '#7c4dff', icon: <DnsIcon fontSize="small" /> },
  cidr: { label: 'CIDR', color: '#00bfa5', icon: <RouteIcon fontSize="small" /> },
  unknown: { label: 'ASSET', color: '#9e9e9e', icon: <LanIcon fontSize="small" /> },
};

const PentestScope = () => {
  const [scopeData, setScopeData] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { pentest_id } = useParams();

  const fetchScope = (token) => {
    fetch(`${BASEURL}/pentest/scope?pentest_id=${pentest_id}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.error) {
          if (data.message === 'Invalid token') navigate('/');
          setError(data.message);
        } else {
          setScopeData(data.scope || '');
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error('Error fetching scope:', err);
        setError('Failed to fetch scope.');
        setLoading(false);
      });
  };

  useEffect(() => {
    const token = sessionStorage.getItem('token');
    if (token) fetchScope(token);
    else {
      console.error('No token found');
      navigate('/');
    }
  }, [navigate, pentest_id]);

  // Parse scope -> assets
  const assets = useMemo(() => {
    const lines = String(scopeData || '')
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter(Boolean);
    return lines.map((line, i) => {
      const c = classifyAsset(line);
      return { id: `${i}-${line}`, raw: line, ...c };
    });
  }, [scopeData]);

  const totals = useMemo(() => {
    const t = { domain: 0, ip: 0, cidr: 0, unknown: 0 };
    assets.forEach((a) => (t[a.type] = (t[a.type] || 0) + 1));
    return t;
  }, [assets]);

  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); } catch {}
  };
  const copyAll = () => copy(assets.map((a) => a.raw).join('\n'));

  // --- UI --------------------------------------------------------
  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', flexDirection: 'column' }}>
      <Nav />

      <Box sx={{ flexGrow: 1, p: { xs: 2, md: 3 } }}>
        {/* KPI Cards row (no hero/header) */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ p: 2, borderRadius: 3, boxShadow: 2 }}>
              <Stack spacing={0.5}>
                <Typography variant="overline" color="text.secondary">DOMAINS</Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip
                    icon={<PublicIcon />}
                    label="DOMAIN"
                    size="small"
                    sx={{ bgcolor: alpha(PRIMARYCOLOR, 0.12), color: PRIMARYCOLOR, border: `1px solid ${alpha(PRIMARYCOLOR, 0.35)}` }}
                  />
                  <Typography variant="h5" sx={{ fontWeight: 900 }}>{totals.domain}</Typography>
                </Stack>
              </Stack>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ p: 2, borderRadius: 3, boxShadow: 2 }}>
              <Stack spacing={0.5}>
                <Typography variant="overline" color="text.secondary">IPS</Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip
                    icon={<DnsIcon />}
                    label="IP"
                    size="small"
                    sx={{ bgcolor: alpha('#7c4dff', 0.12), color: '#7c4dff', border: `1px solid ${alpha('#7c4dff', 0.35)}` }}
                  />
                  <Typography variant="h5" sx={{ fontWeight: 900 }}>{totals.ip}</Typography>
                </Stack>
              </Stack>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ p: 2, borderRadius: 3, boxShadow: 2 }}>
              <Stack spacing={0.5}>
                <Typography variant="overline" color="text.secondary">CIDRS</Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip
                    icon={<RouteIcon />}
                    label="CIDR"
                    size="small"
                    sx={{ bgcolor: alpha('#00bfa5', 0.12), color: '#00bfa5', border: `1px solid ${alpha('#00bfa5', 0.35)}` }}
                  />
                  <Typography variant="h5" sx={{ fontWeight: 900 }}>{totals.cidr}</Typography>
                </Stack>
              </Stack>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ p: 2, borderRadius: 3, boxShadow: 2 }}>
              <Stack spacing={0.5}>
                <Typography variant="overline" color="text.secondary">TOTAL</Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip
                    label="IN SCOPE"
                    icon={<CheckCircleOutlineIcon />}
                    size="small"
                    sx={{ bgcolor: alpha('#2e7d32', 0.12), color: '#2e7d32', border: `1px solid ${alpha('#2e7d32', 0.35)}` }}
                  />
                  <Typography variant="h5" sx={{ fontWeight: 900 }}>{assets.length}</Typography>
                </Stack>
              </Stack>
            </Card>
          </Grid>
        </Grid>

        {/* Scope card list */}
        <Card sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, boxShadow: 3 }}>
          {loading ? (
            <Stack alignItems="center" justifyContent="center" sx={{ py: 6 }}>
              <CircularProgress />
              <Typography variant="body2" sx={{ mt: 2 }} color="text.secondary">
                Loading scope…
              </Typography>
            </Stack>
          ) : error ? (
            <Typography color="error">{error}</Typography>
          ) : assets.length === 0 ? (
            <Paper
              variant="outlined"
              sx={{
                p: 4,
                borderRadius: 3,
                textAlign: 'center',
                borderStyle: 'dashed',
                borderColor: (t) => alpha(t.palette.text.primary, 0.2),
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
                No scope yet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Add 1–3 lines — each a domain, IP, or CIDR range (e.g., <em>example.com</em>, <em>203.0.113.10</em>, <em>203.0.113.0/24</em>).
              </Typography>
            </Paper>
          ) : (
            <>
              <Grid container spacing={2}>
                {assets.map((a) => {
                  const meta = typeMeta[a.type] || typeMeta.unknown;
                  const displayDomain = a.type === 'domain'
                    ? (a.wasWildcard || a.raw.startsWith('*.') ? a.value : a.value) // base domain
                    : null;
                  const wildcardDomain = a.type === 'domain'
                    ? (a.raw.startsWith('*.') ? a.raw : `*.${a.value}`)
                    : null;

                  return (
                    <Grid key={a.id} item xs={12} md={6} lg={4}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          borderRadius: 3,
                          height: '100%',
                          border: '1px solid',
                          borderColor: (t) => alpha(t.palette.divider, 0.6),
                          background:
                            a.type === 'domain'
                              ? alpha(PRIMARYCOLOR, 0.04)
                              : a.type === 'ip'
                              ? alpha('#7c4dff', 0.05)
                              : a.type === 'cidr'
                              ? alpha('#00bfa5', 0.06)
                              : 'transparent',
                        }}
                      >
                        <Stack spacing={1}>
                          <Stack direction="row" alignItems="center" spacing={1} justifyContent="space-between">
                            <Chip
                              icon={meta.icon}
                              label={meta.label}
                              size="small"
                              sx={{
                                bgcolor: alpha(meta.color, 0.12),
                                color: meta.color,
                                fontWeight: 700,
                                border: `1px solid ${alpha(meta.color, 0.35)}`,
                              }}
                            />
                            <Chip
                              size="small"
                              label="IN SCOPE"
                              icon={<CheckCircleOutlineIcon fontSize="small" />}
                              sx={{
                                bgcolor: alpha('#2e7d32', 0.08),
                                color: '#2e7d32',
                                border: '1px solid ' + alpha('#2e7d32', 0.25),
                              }}
                            />
                          </Stack>

                          {/* Primary value */}
                          <Typography
                            variant="h6"
                            sx={{ fontWeight: 900, wordBreak: 'break-word', lineHeight: 1.2 }}
                            title={a.raw}
                          >
                            {a.type === 'domain' ? displayDomain : (a.value || a.raw)}
                          </Typography>

                          {/* Wildcard clarification for domains */}
                          {a.type === 'domain' && (
                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                              <Chip
                                size="small"
                                label="Wildcard"
                                sx={{ bgcolor: alpha(PRIMARYCOLOR, 0.12), color: PRIMARYCOLOR, border: `1px solid ${alpha(PRIMARYCOLOR, 0.3)}` }}
                              />
                              <Typography variant="body2" color="text.secondary">
                                Covers: <strong>{wildcardDomain}</strong> (all subdomains)
                              </Typography>
                            </Stack>
                          )}

                          <Divider sx={{ my: 1 }} />

                          {/* Actions */}
                          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap>
                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                              {a.type === 'domain' && (
                                <Tooltip title="Open in browser">
                                  <IconButton
                                    size="small"
                                    component="a"
                                    href={`https://${displayDomain}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    sx={{ border: '1px solid', borderColor: alpha(meta.color, 0.35), borderRadius: 1.5 }}
                                  >
                                    <LinkIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              <Tooltip title="Copy value">
                                <IconButton
                                  size="small"
                                  onClick={() => copy(a.raw)}
                                  sx={{ border: '1px solid', borderColor: alpha(meta.color, 0.35), borderRadius: 1.5 }}
                                >
                                  <ContentCopyIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Stack>

                            <Typography variant="caption" color="text.secondary">
                              Raw: {a.raw}
                            </Typography>
                          </Stack>
                        </Stack>
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>

              <Divider sx={{ my: 3 }} />
              <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between" spacing={1}>
                <Typography variant="body2" color="text.secondary">
                  {assets.length} item{assets.length !== 1 ? 's' : ''} in scope • Domains {totals.domain} • IPs {totals.ip} • CIDRs {totals.cidr}
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Button variant="outlined" onClick={copyAll} startIcon={<ContentCopyIcon />}>
                    Copy full scope
                  </Button>
                </Stack>
              </Stack>
            </>
          )}
        </Card>
      </Box>
    </Box>
  );
};

export default PentestScope;
